import { useContext, useEffect, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import { authContext } from '../../../../contexts/AuthContext'
import { listUnassignedCompliances, addComplianceToSociety, insertComplianceForSociety } from '../../../../services/societyCompliances'
import ComplianceDialog from '../../Compliances/ComplianceDialog'

// Lets the user pick an existing catalog compliance not yet assigned to this
// society, or define a brand new one (reusing the same ComplianceDialog the
// admin Compliances catalog page uses) and assign it in one step.
export default function AssignComplianceDialog({ open, society, onClose, onAssigned }) {
  const { t } = useTranslation()
  const { auth } = useContext(authContext)
  const [availableCompliances, setAvailableCompliances] = useState([])
  const [selectedCompliance, setSelectedCompliance] = useState(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [newComplianceData, setNewComplianceData] = useState({})

  useEffect(() => {
    if (!open) return
    setSelectedCompliance(null)
    listUnassignedCompliances(society.id)
      .then((data) => setAvailableCompliances(data || []))
      .catch(() => setAvailableCompliances([]))
  }, [open, society.id])

  const handleAssign = async () => {
    if (!selectedCompliance) return
    await addComplianceToSociety(society.id, selectedCompliance.id, auth.user.id)
    onAssigned()
  }

  const handleCreateAndAssign = async () => {
    await insertComplianceForSociety({
      ...newComplianceData,
      society_id: society.id,
      loggedUserId: auth.user.id
    })
    setCreateOpen(false)
    setNewComplianceData({})
    onAssigned()
  }

  return (
    <>
      <Dialog open={open && !createOpen} onClose={onClose} maxWidth='sm' fullWidth>
        <DialogTitle>{t('assign_compliance')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {availableCompliances.length === 0 ? (
            <Typography color='text.secondary'>{t('no_unassigned_compliances')}</Typography>
          ) : (
            <Autocomplete
              options={availableCompliances}
              getOptionLabel={(option) => (society.language === 'es' ? option.name_es : option.name_en) || ''}
              value={selectedCompliance}
              onChange={(e, newValue) => setSelectedCompliance(newValue)}
              renderInput={(params) => (
                <TextField {...params} label={t('Select compliance')} variant='standard' autoFocus />
              )}
            />
          )}
          <Divider />
          <Button
            onClick={() => setCreateOpen(true)}
            sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
          >
            {t('Create & Assign')}
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('Cancel')}</Button>
          <Button
            variant='contained'
            disabled={!selectedCompliance}
            onClick={handleAssign}
            sx={{
              backgroundColor: window.$brand === 'PRB' ? '#17afb6' : '#102a46',
              borderRadius: window.$brand === 'PRB' ? 0 : 5
            }}
          >
            {t('Assign')}
          </Button>
        </DialogActions>
      </Dialog>

      <ComplianceDialog
        open={createOpen}
        compliance={newComplianceData}
        errors={{}}
        onClose={() => {
          setCreateOpen(false)
          setNewComplianceData({})
        }}
        onSave={handleCreateAndAssign}
        onChange={(updated) => setNewComplianceData((prev) => ({ ...prev, ...updated }))}
      />
    </>
  )
}
